import React, { useEffect } from "react";
import { useAppSettings } from "../../../hooks/useAppSettings.tsx";
import { useNavigate, useParams } from "react-router-dom";
import { readPositions } from "../../../services/position.service.ts";
import { useDispatch } from "react-redux";
import {
  setPositions,
  setPositionsOriginal,
} from "../../../store/slices/sucursalesSlice.ts";
import {
  readPositionsByBranch,
  readTemplates,
  updatePositions,
} from "../../../services/branchSchedule.service.ts";
import {
  readBranch,
  saveBranch,
  updateBranch,
} from "../../../services/branch.service.ts";
import { notification } from "../../../utils/Notifications.tsx";
import Layout from "../../Layout.tsx";
import Title from "../../Title.tsx";
import { Panel, PanelHeader } from "../../panel/panel.jsx";
import SucursalFormGeneral from "./SucursalFormGeneral.tsx";
import { useFormik } from "formik";
import SucursalesFormPuestos from "./SucursalesFormPuestos.tsx";
import BranchSelector from "./componentes/BranchSelector.tsx";
import Button from "../../Button.tsx";
import { BranchTemplateSchema } from "../../../validation/schemas/BranchTemplateSchema.ts";
import { readBusinessNames } from "../../../services/businessName.service.ts";

const SucursalesForm = () => {
  useAppSettings();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [loadingPositions, setLoadingPositions] = React.useState(true);
  const [editing, setEditing] = React.useState<any>(null);
  const [templates, setTemplates] = React.useState<any>(null);
  const [businessNames, setBusinessNames] = React.useState<any>(null);
  const { id } = useParams();

  useEffect(() => {
    const loadDependencies = async () => {
      setLoading(true);
      try {
        const positions = await readPositions();
        dispatch(setPositions(positions));
        dispatch(setPositionsOriginal(positions));

        const templatesData = await readTemplates();
        setTemplates(templatesData);

        const bussinessNames = await readBusinessNames();
        setBusinessNames(bussinessNames);

        if (id) {
          setLoadingPositions(true);
          const branch = await readBranch(Number(id));
          setEditing(branch);

          const positionsA = await readPositionsByBranch(Number(id));

          if (
            !positionsA ||
            !Array.isArray(positionsA) ||
            positionsA.length === 0
          ) {
            formik.setFieldValue("puestos", []);
            setLoadingPositions(false);
            return;
          }

          const positionsByPuesto = positionsA.reduce((acc, current) => {
            if (!current || !current.position || !current.position.id) {
              return acc;
            }

            const positionId = current.position.id;
            if (!acc[positionId]) {
              acc[positionId] = [];
            }
            acc[positionId].push(current);
            return acc;
          }, {});

          const groupedPositions = (Object.values(positionsByPuesto) as any[])
            .map((positionGroup: any[]) => {
              if (!positionGroup || positionGroup.length === 0) return null;

              const firstPosition = positionGroup[0];
              const positionId = firstPosition.position.id;

              // Obtener valores booleanos del backend, asegurando que null/undefined se conviertan en false explícitamente
              // Solo usar true si explícitamente es true en el backend
              const showScheduleValue = firstPosition.showSchedule === true;
              const isHireValue = firstPosition.isHire === true;
              const isEssencialValue = firstPosition.isEssencial === true;

              let matutino = 0;
              let intermedio = 0;
              let vespertino = 0;

              positionGroup.forEach((p) => {
                if (p.shift && typeof p.shift === "string") {
                  if (p.shift.includes("Matutino")) {
                    matutino += Number(p.numberOfWorkers) || 0;
                  }
                  if (p.shift.includes("Intermedio")) {
                    intermedio += Number(p.numberOfWorkers) || 0;
                  }
                  if (p.shift.includes("Vespertino")) {
                    vespertino += Number(p.numberOfWorkers) || 0;
                  }
                }
              });

              return {
                puesto: positionId,
                matutino,
                intermedio,
                vespertino,
                showSchedule: showScheduleValue,
                isHire: isHireValue,
                isEssencial: isEssencialValue,
              };
            })
            .filter((p) => p !== null);

          if (groupedPositions && groupedPositions.length > 0) {
            formik.setFieldValue("puestos", groupedPositions);
          } else {
            formik.setFieldValue("puestos", []);
          }
          setLoadingPositions(false);
        } else {
          setLoadingPositions(false);
        }
      } catch (error) {
        notification(
          "Error al cargar los puestos",
          "Sucedió algo al intentar cargar los puestos",
          "danger"
        );
      } finally {
        setLoading(false);
      }
    };

    loadDependencies();
  }, [id]);

  const formik = useFormik({
    initialValues: {
      name: editing?.name ?? "",
      branchTemplate: editing?.templateBranch ?? -99,
      branch: editing?.branch ?? "",
      templates: templates ?? [],
      puestos: editing?.puestos ?? [],
      idBusinessName: -99,
      isRestaurant: editing?.isRestaurant ?? false,
    },
    validationSchema: BranchTemplateSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const { name, idBusinessName, branchTemplate, isRestaurant } = values;

        let branch;

        if (editing) {
          branch = await updateBranch(Number(id), {
            name,
            templateBranch: branchTemplate,
            isRestaurant: isRestaurant ?? false,
          });
        } else {
          branch = await saveBranch({
            name,
            idBusinessName,
            templateBranch: branchTemplate,
            isRestaurant: isRestaurant ?? false,
          });
        }

        await updatePositions(branch.id, { puestos: values.puestos });
        notification(
          "Sucursal guardada",
          "La sucursal se guardó correctamente",
          "success"
        );
      } catch (error) {
        console.error(error);
        notification(
          "Error al guardar la sucursal",
          "Sucedió algo al intentar guardar la sucursal",
          "danger"
        );
      } finally {
        navigate("/sucursales");
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    if (editing) {
      formik.setFieldValue("name", editing.name);
      formik.setFieldValue("branchTemplate", editing.templateBranch?.id);
      formik.setFieldValue("branch", editing.branch);
      formik.setFieldValue("isRestaurant", editing.isRestaurant ?? false);
    }
  }, [editing]);

  return (
    <Layout loading={loading}>
      <Title
        baseTitle="Sucursales"
        basePath="/sucursales"
        activeTitle={`${id ? "Editar" : "Nueva"} sucursal`}
        title={`${id ? "Editar" : "Nueva"} sucursal`}
      />
      {id && (
        <div className="card border-0 m-4 rounded overflow-hidden rounded-3">
          <div className="text-white p-3" style={{ background: "#495057" }}>
            <h5 className="mb-0" style={{ fontSize: "12px" }}>
              Formulario de búsqueda
            </h5>
          </div>
          <div className="p-4">
            <BranchSelector currentId={id} />
          </div>
        </div>
      )}
      <div className="card border-0 m-4 rounded">
        <Panel>
          <PanelHeader noButton={true} className="bg-azul">
            Datos generales de la sucursal
          </PanelHeader>
          <SucursalFormGeneral
            formik={formik}
            businessNames={businessNames}
            editing={!!editing}
          />
        </Panel>
      </div>
      <div className="card border-0 m-4 rounded">
        <Panel>
          <PanelHeader noButton={true} className="bg-azul">
            Puestos de la sucursal
          </PanelHeader>
          <SucursalesFormPuestos
            error={formik.errors.puestos}
            formik={formik}
            templates={templates}
            loadingPositions={loadingPositions}
          />
        </Panel>
      </div>
      <div className="card border-0 m-4 rounded p-3">
        <div className="row justify-content-end">
          <div className="col-md-3">
            <Button
              onClick={() => navigate("/sucursales")}
              title="Cancelar"
              variant="secondary"
              type="button"
            />
          </div>
          <div className="col-md-3">
            <Button
              onClick={formik.handleSubmit}
              title="Guardar"
              variant="azul"
              type="submit"
              disabled={loading}
            />
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SucursalesForm;
